import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as d3 from 'd3';
import './BubblesVisualization.css';

// Color functions based on price change
const getColor = (change) => {
  if (Math.abs(change) < 0.1) return 'neutral';
  return change > 0 ? 'up' : 'down';
};

const getGradientId = (colorType) => {
  return `grad-${colorType}`;
};

function BubblesVisualization({ data, mode, sortBy = 'volume', onCategoryClick }) {
  const { t } = useTranslation();
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const zoomRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [tooltip, setTooltip] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);

  // Use refs for callbacks to avoid re-rendering D3 on every tooltip change
  const onCategoryClickRef = useRef(onCategoryClick);
  onCategoryClickRef.current = onCategoryClick;

  // Handle resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: Math.max(width || 800, 400),
          height: Math.max(height || 600, 400)
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Format price for tooltip
  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    if (price >= 1000) return `$${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    if (price >= 1) return `$${price.toFixed(2)}`;
    if (price >= 0.01) return `$${price.toFixed(4)}`;
    if (price >= 0.0001) return `$${price.toFixed(6)}`;
    return `$${price.toFixed(8)}`;
  };

  // Format volume
  const formatVolume = (volume) => {
    if (!volume && volume !== 0) return '-';
    if (volume >= 1e12) return `$${(volume / 1e12).toFixed(2)}T`;
    if (volume >= 1e9) return `$${(volume / 1e9).toFixed(2)}B`;
    if (volume >= 1e6) return `$${(volume / 1e6).toFixed(2)}M`;
    if (volume >= 1e3) return `$${(volume / 1e3).toFixed(0)}K`;
    return `$${volume.toFixed(0)}`;
  };

  // Calculate aggregates for sectors
  const processedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return null;

    if (mode === 'sectors') {
      // For sectors, calculate aggregated values
      return data.map(sector => {
        if (!sector.children || sector.children.length === 0) {
          return { ...sector, aggregatedChange: 0, value: 0.1 };
        }

        let totalAbsChange = 0;
        let weightedChangeSum = 0;

        sector.children.forEach(coin => {
          const absChange = Math.abs(coin.change || 0);
          totalAbsChange += absChange;
          weightedChangeSum += absChange * (coin.change || 0);
        });

        // Weighted average change (for color)
        const aggregatedChange = totalAbsChange === 0 ? 0 : weightedChangeSum / totalAbsChange;

        // Average absolute change (for bubble SIZE)
        // This ensures sectors with stronger price movements are bigger,
        // regardless of how many coins they contain
        const avgAbsChange = totalAbsChange / sector.children.length;

        return {
          ...sector,
          value: Math.max(avgAbsChange, 0.1), // Bubble size = average |change|
          aggregatedChange: Math.round(aggregatedChange * 100) / 100,
        };
      });
    }

    // For top100, just return data with proper values
    return data.map(coin => ({
      ...coin,
      value: Math.abs(coin.change || 0) || 0.1, // Minimum value for visibility
    }));
  }, [data, mode]);

  // Draw bubbles
  useEffect(() => {
    if (!processedData || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { width, height } = dimensions;
    const margin = 20;

    // Create hierarchical data structure
    // IMPORTANT: Destructure 'children' to prevent D3 from recursively summing child values
    // D3's .sum() traverses 'children' arrays, which would make bubble size = sum of all coins' values
    // instead of the sector's avgAbsChange
    const hierarchyData = {
      name: 'root',
      children: processedData.map(item => {
        const { children, ...rest } = item;
        return {
          ...rest,
          value: Math.max(item.value || 0.1, 0.1),
          _childCoins: children, // Store separately for "X Assets" display
        };
      }),
    };

    // Create hierarchy and pack layout
    const root = d3.hierarchy(hierarchyData)
      .sum(d => d.value || 0)
      .sort((a, b) => {
        if (sortBy === 'change') {
          const valA = mode === 'top100' ? Math.abs(a.data.change || 0) : Math.abs(a.data.aggregatedChange || 0);
          const valB = mode === 'top100' ? Math.abs(b.data.change || 0) : Math.abs(b.data.aggregatedChange || 0);
          return valB - valA;
        }
        return (b.value || 0) - (a.value || 0);
      });

    const pack = d3.pack()
      .size([width - margin * 2, height - margin * 2])
      .padding(mode === 'top100' ? 6 : 20);

    pack(root);

    // Create defs for gradients
    const defs = svg.append('defs');

    // Green gradient (up)
    const gradUp = defs.append('linearGradient')
      .attr('id', 'grad-up')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '100%').attr('y2', '100%');
    gradUp.append('stop').attr('offset', '0%').attr('stop-color', '#10b981');
    gradUp.append('stop').attr('offset', '100%').attr('stop-color', '#047857');

    // Red gradient (down)
    const gradDown = defs.append('linearGradient')
      .attr('id', 'grad-down')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '100%').attr('y2', '100%');
    gradDown.append('stop').attr('offset', '0%').attr('stop-color', '#ef4444');
    gradDown.append('stop').attr('offset', '100%').attr('stop-color', '#b91c1c');

    // Neutral gradient
    const gradNeutral = defs.append('linearGradient')
      .attr('id', 'grad-neutral')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '100%').attr('y2', '100%');
    gradNeutral.append('stop').attr('offset', '0%').attr('stop-color', '#4b5563');
    gradNeutral.append('stop').attr('offset', '100%').attr('stop-color', '#1f2937');

    // Create zoom container group
    const zoomGroup = svg.append('g')
      .attr('class', 'zoom-container');

    // Create content group inside zoom container
    const g = zoomGroup.append('g')
      .attr('transform', `translate(${margin}, ${margin})`);

    // Get nodes to render (depth 1 = direct children of root)
    const nodesToRender = root.children || [];

    // Draw bubbles
    const nodeGroups = g.selectAll('g.node-group')
      .data(nodesToRender)
      .enter()
      .append('g')
      .attr('class', 'node-group')
      .attr('transform', d => `translate(${d.x}, ${d.y})`);

    // Content layer with hover effects
    const content = nodeGroups.append('g')
      .attr('class', 'scale-layer')
      .style('cursor', mode === 'sectors' ? 'pointer' : 'default');

    // Circles
    content.append('circle')
      .attr('r', d => d.r)
      .attr('fill', d => {
        const change = mode === 'top100' ? d.data.change : d.data.aggregatedChange;
        return `url(#${getGradientId(getColor(change || 0))})`;
      })
      .attr('stroke', d => {
        const change = mode === 'top100' ? d.data.change : d.data.aggregatedChange;
        if (Math.abs(change || 0) < 0.1) return '#6b7280';
        return change > 0 ? '#34d399' : '#f87171';
      })
      .attr('stroke-width', 1.5)
      .attr('stroke-opacity', 0.5);

    // Function to update text visibility based on zoom scale
    const updateTextVisibility = (scale) => {
      const minRadius = mode === 'top100' ? 12 : 20;
      const effectiveMinRadius = minRadius / scale;

      // Update title visibility
      content.selectAll('.bubble-label-title')
        .style('display', function() {
          const d = d3.select(this.parentNode).datum();
          return d.r >= effectiveMinRadius ? 'block' : 'none';
        })
        .style('font-size', function() {
          const d = d3.select(this.parentNode).datum();
          if (mode === 'top100') return Math.min(d.r / 2.5, 14 / scale) + 'px';
          return Math.min(d.r / 4, 22 / scale) + 'px';
        });

      // Update value visibility
      content.selectAll('.bubble-label-value')
        .style('display', function() {
          const d = d3.select(this.parentNode).datum();
          return d.r >= effectiveMinRadius ? 'block' : 'none';
        })
        .style('font-size', function() {
          const d = d3.select(this.parentNode).datum();
          if (mode === 'top100') return Math.min(d.r / 3.5, 10 / scale) + 'px';
          return Math.min(d.r / 5, 16 / scale) + 'px';
        });

      // Update assets count visibility (sectors only)
      if (mode === 'sectors') {
        content.selectAll('.bubble-label-assets')
          .style('display', function() {
            const d = d3.select(this.parentNode).datum();
            return d.r >= 35 / scale ? 'block' : 'none';
          })
          .style('font-size', function() {
            const d = d3.select(this.parentNode).datum();
            return Math.max(8, d.r / 9) / scale + 'px';
          });
      }
    };

    // Add all text elements initially (hidden by default for small bubbles)
    // Name/Symbol - add to ALL nodes
    content.append('text')
      .attr('dy', mode === 'sectors' ? '-0.2em' : '-0.2em')
      .attr('class', 'bubble-label-title')
      .style('text-anchor', 'middle')
      .style('font-size', d => {
        if (mode === 'top100') return Math.min(d.r / 2.5, 14) + 'px';
        return Math.min(d.r / 4, 22) + 'px';
      })
      .text(d => d.data.name || d.data.symbol);

    // Change percentage - add to ALL nodes
    content.append('text')
      .attr('dy', '1.1em')
      .attr('class', 'bubble-label-value')
      .style('text-anchor', 'middle')
      .style('font-size', d => {
        if (mode === 'top100') return Math.min(d.r / 3.5, 10) + 'px';
        return Math.min(d.r / 5, 16) + 'px';
      })
      .text(d => {
        const change = mode === 'top100' ? d.data.change : d.data.aggregatedChange;
        const val = change || 0;
        return (val >= 0 ? '+' : '') + val.toFixed(1) + '%';
      });

    // Assets count (sectors only) - add to ALL sector nodes
    if (mode === 'sectors') {
      content.append('text')
        .attr('dy', '2.6em')
        .attr('class', 'bubble-label-assets')
        .style('text-anchor', 'middle')
        .style('font-size', d => Math.max(8, d.r / 9) + 'px')
        .text(d => d.data._childCoins ? `${d.data._childCoins.length} Assets` : '');
    }

    // Apply initial visibility
    updateTextVisibility(1);

    // Setup zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.5, 5]) // Min 0.5x, Max 5x zoom
      .on('zoom', (event) => {
        zoomGroup.attr('transform', event.transform);
        setZoomScale(event.transform.k);
        updateTextVisibility(event.transform.k);
      });

    // Apply zoom to SVG
    svg.call(zoom)
      .on('dblclick.zoom', null); // Disable double-click zoom

    // Store zoom reference for reset
    zoomRef.current = zoom;

    // Event handlers
    content
      .on('mouseenter', function(event, d) {
        const rect = containerRef.current.getBoundingClientRect();
        const change = mode === 'top100' ? d.data.change : d.data.aggregatedChange;

        setTooltip({
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
          data: d.data,
          change: change,
          isSector: mode === 'sectors',
        });
      })
      .on('mousemove', function(event) {
        const rect = containerRef.current.getBoundingClientRect();
        setTooltip(prev => prev ? {
          ...prev,
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        } : null);
      })
      .on('mouseleave', function() {
        setTooltip(null);
      })
      .on('click', function(event, d) {
        event.stopPropagation();
        setTooltip(null); // Hide tooltip on click
        if (mode === 'sectors' && d.data._childCoins && onCategoryClickRef.current) {
          onCategoryClickRef.current(d.data);
        }
      });

    // Reset zoom when mode changes
    return () => {
      setZoomScale(1);
    };
  }, [processedData, mode, sortBy, dimensions]); // Removed tooltip and onCategoryClick from deps

  // Reset zoom function
  const resetZoom = useCallback(() => {
    if (svgRef.current && zoomRef.current) {
      const svg = d3.select(svgRef.current);
      svg.transition().duration(300).call(zoomRef.current.transform, d3.zoomIdentity);
    }
  }, []);

  // Tooltip positioning
  const getTooltipStyle = useCallback(() => {
    if (!tooltip) return {};

    let x = tooltip.x + 15;
    let y = tooltip.y - 10;

    // Adjust if tooltip goes off screen
    const tooltipWidth = 220;
    const tooltipHeight = 150;

    if (x + tooltipWidth > dimensions.width) {
      x = tooltip.x - tooltipWidth - 10;
    }
    if (y + tooltipHeight > dimensions.height) {
      y = tooltip.y - tooltipHeight - 10;
    }
    if (y < 0) y = 10;

    return { left: x, top: y };
  }, [tooltip, dimensions]);

  if (!data) {
    return (
      <div className="bubbles-visualization-container" ref={containerRef}>
        <div className="bubbles-loading">
          <div className="loading-spinner"></div>
          <span>{t('bubbles.loading', 'Loading bubbles...')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bubbles-visualization-container" ref={containerRef}>
      {/* SVG Canvas */}
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        className="bubbles-svg"
      />

      {/* Tooltip */}
      {tooltip && (
        <div
          className="bubble-tooltip glass-panel"
          style={getTooltipStyle()}
        >
          <div className="tooltip-header-row">
            <span className="tooltip-type-badge">
              {tooltip.isSector ? 'SECTOR' : 'ASSET'}
            </span>
            {!tooltip.isSector && tooltip.data.price !== undefined && (
              <span className="tooltip-price">{formatPrice(tooltip.data.price)}</span>
            )}
          </div>

          <div className="tooltip-main-row">
            <span className="tooltip-name">{tooltip.data.name || tooltip.data.symbol}</span>
            <div className="tooltip-change-container">
              <span className={`tooltip-change ${(tooltip.change || 0) >= 0 ? 'positive' : 'negative'}`}>
                {(tooltip.change || 0) >= 0 ? '▲' : '▼'} {Math.abs(tooltip.change || 0).toFixed(2)}%
              </span>
            </div>
          </div>

          {!tooltip.isSector ? (
            <div className="tooltip-details-grid">
              <div className="tooltip-detail">
                <span className="tooltip-detail-label">24h Vol</span>
                <span className={`tooltip-detail-value ${(tooltip.data.volChange || 0) >= 0 ? 'positive' : 'negative'}`}>
                  {(tooltip.data.volChange || 0) >= 0 ? '+' : ''}{(tooltip.data.volChange || 0).toFixed(1)}%
                </span>
              </div>
              <div className="tooltip-detail text-right">
                <span className="tooltip-detail-label">Volume</span>
                <span className="tooltip-detail-value">{formatVolume(tooltip.data.volume)}</span>
              </div>
            </div>
          ) : (
            <div className="tooltip-sector-hint">
              <svg className="hint-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Click to inspect assets</span>
            </div>
          )}
        </div>
      )}

      {/* Zoom Controls */}
      <div className="bubbles-zoom-controls">
        <span className="zoom-level">{Math.round(zoomScale * 100)}%</span>
        {zoomScale !== 1 && (
          <button className="zoom-reset-btn" onClick={resetZoom} title="Reset zoom">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
          </button>
        )}
      </div>

      {/* Zoom hint */}
      {zoomScale === 1 && (
        <div className="bubbles-zoom-hint">
          Scroll to zoom • Drag to pan
        </div>
      )}

      {/* Legend */}
      <div className="bubbles-legend">
        <div className="legend-item">
          <div className="legend-dot gain"></div>
          <span>Gain</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot loss"></div>
          <span>Loss</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot neutral"></div>
          <span>Neutral</span>
        </div>
      </div>
    </div>
  );
}

export default BubblesVisualization;
